import { resolveFieldDefinitionName } from "./field-identity";
import type { DocumentPayload, FieldState, PageState, FieldGroupState } from "../state/types";

/**
 * F-097 — extracted from VerificationStation.tsx so the prefetch layer can
 * map raw GET /documents/:id responses at cache-store time. Before this,
 * PrefetchManager cached the RAW API payload and the cache-hit path fed it
 * to loadDocument unmapped — every mapped FieldState property (label,
 * dropdownOptions, forceUppercase, boundingBox, placeholder, helpText, …)
 * was absent or wrong on prefetch cache hits.
 *
 * `mapApiToPayload` is the ONE raw→store mapping; every raw ApiDocument is
 * mapped exactly once at fetch time (VerificationStation's fresh-fetch path,
 * PrefetchManager.prefetch's store path).
 */

// API response shape (Prisma includes)
export interface ApiDocument {
  id: string;
  /** R-132 — structured doc identifier (e.g. NOW_20260523_0001_03). */
  displayId?: string | null;
  /** R-167 WU-7 — operator-rendered frozen label, null without a template. */
  customDisplayLabel?: string | null;
  batchId: string;
  filename: string;
  classification: string | null;
  currentStep: string;
  /** F-046 — the current step's re-verification policy (server-resolved). */
  stepReverifyPolicy?: "standard" | "only_failures" | "only_unverified";
  docIndex: number;
  totalDocs: number;
  batch: {
    id: string;
    name: string;
    /** R-132 / R-167 WU-7 — batch identifier fields for the VS header label. */
    displayId?: string | null;
    customDisplayLabel?: string | null;
    jobTypeId: string | null;
    jobType?: { forceUppercase: boolean } | null;
  };
  pages: Array<{
    id: string;
    pageNumber: number;
    imagePath: string;
    imageUrl: string | null;
    thumbnailPath: string | null;
    thumbnailUrl: string | null;
    width: number;
    height: number;
    status: string;
  }>;
  fields: Array<{
    id: string;
    fieldDefinitionId: string;
    ocrValue: string | null;
    originalOcrValue: string | null;
    ocrConfidence: number | null;
    keyedValue: string | null;
    validationStatus: string;
    isOverridden: boolean;
    overrideReason: string | null;
    // R-139 / R-176 / R-177 — `document_fields.bounding_box_*` columns.
    // Populated by the worker's upsert after the OCR scan returns a
    // bounding box (zoned mode always; fullpage now too, post OCR e34cbad).
    boundingBoxX?: number | null;
    boundingBoxY?: number | null;
    boundingBoxWidth?: number | null;
    boundingBoxHeight?: number | null;
    boundingBoxPage?: number | null;
    fieldDefinition: {
      id: string;
      /** Stable identity (the legacy `name` column was dropped). */
      internalName: string;
      /** @deprecated legacy column, may be absent — kept for back-compat reads. */
      name?: string;
      label: string;
      dataType: string;
      displayOrder: number;
      isRequired: boolean;
      groupId: string | null;
      // R-177 — admin-drawn extraction zone (JSON column). Rendered as an
      // always-on overlay in the VS so operators can see the configured
      // extraction region for each field.
      zoneDefinition?: {
        pageNumber: number;
        x: number;
        y: number;
        width: number;
        height: number;
      } | null;
    };
    /**
     * R-239 P4 — re-verify valve runtime flag. Computed server-side in
     * GET /api/documents/:id from the job type's reverifyValve +
     * FieldLearningMetric.readsSinceForced.
     */
    forceReverify?: boolean;
    readOnly?: boolean;
    /**
     * F-046 — upstream human-verification record (full DocumentField row
     * spread). Previously present in the response but dropped by this mapper.
     */
    isVerified?: boolean;
    verifiedBy?: string | null;
    verifiedAt?: string | null;
  }>;
  groups: Array<{
    id: string;
    name: string;
    type: string;
    displayOrder: number;
    layoutColumns: number;
    isCollapsible: boolean;
    isCollapsedByDefault: boolean;
  }>;
  lineItems: Array<{
    id: string;
    lineItemDefId: string;
    rowNumber: number;
    fields: Array<{
      id: string;
      columnName: string;
      ocrValue: string | null;
      ocrConfidence: number | null;
      keyedValue: string | null;
      validationStatus: string;
    }>;
  }>;
  lineItemDefinitions?: Array<{
    id: string;
    name: string;
    groupId: string;
    columns: unknown; // JSON column — shape validated at hydration time
    minRows: number;
    maxRows: number;
  }>;
}

/**
 * R-142 — coerce the Prisma JSONB column into the array shape FieldState
 * expects. The admin writer stores `[{label,value}]`; tolerate `[{value}]`
 * (label falls back to value) and `[string]` (value is the string).
 * Returns null when the field has no options or the column is non-array.
 */
function normalizeDropdownOptions(raw: unknown): Array<{ label: string; value: string }> | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: Array<{ label: string; value: string }> = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      out.push({ label: entry, value: entry });
    } else if (entry && typeof entry === "object") {
      const obj = entry as Record<string, unknown>;
      const value = typeof obj["value"] === "string" ? obj["value"] : undefined;
      const label = typeof obj["label"] === "string" ? obj["label"] : value;
      if (value !== undefined && label !== undefined) out.push({ label, value });
    }
  }
  return out.length > 0 ? out : null;
}

/**
 * Maps a raw GET /documents/:id response (Prisma includes) into the
 * verification store's DocumentPayload shape.
 *
 * Also exposed from VerificationStation.tsx as `mapApiToPayloadForTest`
 * (a legacy unit-test seam). WU-3 / T3.7 added explicit precedence tests
 * for `forceUppercase` there.
 */
export function mapApiToPayload(doc: ApiDocument): DocumentPayload {
  const pages: PageState[] = doc.pages.map((p) => ({
    id: p.id,
    pageNumber: p.pageNumber,
    imageUrl: p.imageUrl ?? p.imagePath,
    thumbnailUrl: p.thumbnailUrl ?? p.thumbnailPath ?? "",
    imageBitmap: null,
    width: p.width,
    height: p.height,
    status: p.status,
  }));

  const fields: FieldState[] = doc.fields
    .sort((a, b) => a.fieldDefinition.displayOrder - b.fieldDefinition.displayOrder)
    .map((f, i) => ({
      id: f.id,
      definitionId: f.fieldDefinitionId,
      groupId: f.fieldDefinition.groupId ?? "",
      groupName: "",
      label: f.fieldDefinition.label,
      name: resolveFieldDefinitionName(f.fieldDefinition),
      dataType: f.fieldDefinition.dataType,
      value: f.keyedValue ?? f.ocrValue ?? "",
      originalOcrValue: f.originalOcrValue ?? f.ocrValue ?? null,
      ocrConfidence: f.ocrConfidence,
      validationStatus: (f.validationStatus as FieldState["validationStatus"]) ?? "pending",
      validationMessage: null,
      overrideBehavior: "block" as const,
      isOverridden: f.isOverridden,
      overrideReason: f.overrideReason,
      isDirty: false,
      // R-144 — operator hasn't tabbed past yet on freshly loaded docs.
      isAcknowledged: false,
      // R-139 / R-176 — OCR-detected box from document_fields.bounding_box_*.
      // Previously hardcoded to null, which meant the VS rendered NO OCR
      // boxes for any document despite Tesseract returning coordinates and
      // the worker writing them to the table. Map all four coords + page.
      // Returns null when any coord is missing (worker writes them as a set
      // or not at all).
      boundingBox:
        f.boundingBoxX != null &&
        f.boundingBoxY != null &&
        f.boundingBoxWidth != null &&
        f.boundingBoxHeight != null &&
        f.boundingBoxPage != null
          ? {
              x: f.boundingBoxX,
              y: f.boundingBoxY,
              width: f.boundingBoxWidth,
              height: f.boundingBoxHeight,
              pageNumber: f.boundingBoxPage,
            }
          : null,
      // R-177 — admin-drawn extraction zone from FieldDefinition.zoneDefinition.
      // Always-on overlay; mirrors the Zones-tab convention so operators see
      // configured-vs-detected divergence at a glance.
      zoneDefinition: (() => {
        const z = (f.fieldDefinition as Record<string, unknown>).zoneDefinition as
          | { pageNumber: number; x: number; y: number; width: number; height: number }
          | null
          | undefined;
        return z && typeof z === "object" && "pageNumber" in z
          ? {
              x: z.x,
              y: z.y,
              width: z.width,
              height: z.height,
              pageNumber: z.pageNumber,
            }
          : null;
      })(),
      needsAttention:
        (f.ocrConfidence !== null && f.ocrConfidence < 0.7) ||
        (f.fieldDefinition.isRequired && !(f.keyedValue ?? f.ocrValue)),
      required: f.fieldDefinition.isRequired,
      tabOrder: f.fieldDefinition.displayOrder ?? i,
      verificationPolicy: (f.fieldDefinition as Record<string, unknown>).verificationPolicy as FieldState["verificationPolicy"] ?? "auto",
      // R-32 P2: per-field verify-threshold override (null = inherit JT).
      // Resolved by resolveEffectiveThreshold in the store + FieldInput.
      verifyThreshold:
        ((f.fieldDefinition as Record<string, unknown>).verifyThreshold as number | null | undefined) ?? null,
      // WU-3 / R-11 — field-level overrides job-type-level via nullish-
      // coalescing (`??`), not OR (`||`). The previous `||` collapsed
      // "field=false (explicit override)" with "field=undefined (no
      // override)", so a per-field opt-out was silently ignored when
      // the job-type had `forceUppercase: true`. The Prisma column is
      // non-nullable boolean, but the snapshot path collapses `false →
      // undefined` (see `snapshot-builder.ts`); the API surface read
      // here keeps the same tri-state contract.
      forceUppercase:
        ((f.fieldDefinition as Record<string, unknown>).forceUppercase as boolean | undefined)
        ?? doc.batch?.jobType?.forceUppercase
        ?? false,
      // E-021 — per-field INPUT key standard (e.g. separatorless MMDDYYYY).
      // Read straight off the FieldDefinition column (GET includes the full
      // relation). null = inherit job-type/tenant default → no client-side
      // normalization (back-compat).
      keyStandard:
        ((f.fieldDefinition as Record<string, unknown>).keyStandard as string | null | undefined) ?? null,
      maxLength: ((f.fieldDefinition as Record<string, unknown>).maxLength as number) ?? null,
      isHandwriting: (f as Record<string, unknown>).isHandwriting === true,
      // Handwriting Phase 3 — engine-disagreement candidates from the
      // ocrHandwritingDiag Json column. Defensive read: only a diag with BOTH
      // candidate strings present maps (fusion populates them solely on
      // disagreement); everything else — absent diag, fused/gate-off null
      // candidates, malformed shapes — collapses to null (pre-WU behavior).
      handwritingCandidates: (() => {
        const diag = (f as Record<string, unknown>).ocrHandwritingDiag as
          | { engine?: unknown; tesseractCandidate?: unknown; trOcrCandidate?: unknown }
          | null
          | undefined;
        return diag &&
          typeof diag.engine === "string" &&
          typeof diag.tesseractCandidate === "string" &&
          typeof diag.trOcrCandidate === "string"
          ? {
              engine: diag.engine,
              tesseract: diag.tesseractCandidate,
              trocr: diag.trOcrCandidate,
            }
          : null;
      })(),
      // ADR-013 — per-character confidence spans + their source text from the
      // same diag. Defensive: a well-formed non-empty spans array AND the
      // source text must both be present; anything else collapses to null
      // (pre-WU behavior). Parsed independently of the candidates above — a
      // spans-only diag (value overlay discarded worker-side) still maps.
      charConfidences: (() => {
        const diag = (f as Record<string, unknown>).ocrHandwritingDiag as
          | { charConfidences?: unknown; charConfidenceText?: unknown }
          | null
          | undefined;
        const spans = diag?.charConfidences;
        const text = diag?.charConfidenceText;
        const wellFormed =
          typeof text === "string" &&
          Array.isArray(spans) &&
          spans.length > 0 &&
          spans.every(
            (s: unknown) =>
              typeof (s as Record<string, unknown>)?.start === "number" &&
              typeof (s as Record<string, unknown>)?.length === "number" &&
              typeof (s as Record<string, unknown>)?.confidence === "number",
          );
        return wellFormed
          ? {
              text,
              spans: spans as Array<{ start: number; length: number; confidence: number }>,
            }
          : null;
      })(),
      // Lane C3 — CAR/LAR check-amount keying aid from the ocrCheckAmountDiag
      // Json column. Surfaced ONLY on the agreement:"none" outcome (no pre-fill
      // was applied — the blind-keying case) AND when at least one side read a
      // cents value to offer. A confirming agreement means the ocrValue pre-fill
      // already covers the field, so it collapses to null (show nothing extra).
      // Defensive: absent diag, non-"none" agreement, and malformed shapes all
      // collapse to null (pre-WU DOM). Each side maps only when its cents is a
      // finite number; a side with a null cents (unreadable) is dropped.
      checkAmountCandidates: (() => {
        const diag = (f as Record<string, unknown>).ocrCheckAmountDiag as
          | { car?: unknown; lar?: unknown; agreement?: unknown }
          | null
          | undefined;
        if (!diag || diag.agreement !== "none") return null;
        const side = (raw: unknown): { cents: number; confidence: number } | null => {
          const s = raw as { cents?: unknown; confidence?: unknown } | null | undefined;
          return s && typeof s.cents === "number" && typeof s.confidence === "number"
            ? { cents: s.cents, confidence: s.confidence }
            : null;
        };
        const car = side(diag.car);
        const lar = side(diag.lar);
        // Nothing readable to offer → no aid.
        return car || lar ? { car, lar } : null;
      })(),
      autoCorrected: (f as Record<string, unknown>).autoCorrected === true,
      preCorrectionValue: ((f as Record<string, unknown>).preCorrectionValue as string) ?? null,
      correctionSource: ((f as Record<string, unknown>).correctionSource as string) ?? null,
      // R-142 — surface dropdown options so FieldInput can render a real <select>
      // instead of a freeform text input. JSONB column on FieldDefinition.
      dropdownOptions: normalizeDropdownOptions((f.fieldDefinition as Record<string, unknown>).dropdownOptions),
      // WU-1.5 — authored field guidance (admin DisplaySection → FieldDefinition
      // columns). Previously persisted but never read by the VS renderer.
      placeholder:
        ((f.fieldDefinition as Record<string, unknown>).placeholder as string | null | undefined) ?? null,
      helpText:
        ((f.fieldDefinition as Record<string, unknown>).helpText as string | null | undefined) ?? null,
      // R-239 P4 — runtime valve flag from the GET response. undefined/false
      // when the valve is off or this field is not being forced this round.
      forceReverify: f.forceReverify === true ? true : undefined,
      // R-239 plan 3b — derived side of a parse binding renders read-only.
      readOnly: f.readOnly === true,
      // F-046 — upstream human-verification record, previously dropped here.
      isVerified: f.isVerified === true,
      verifiedBy: f.verifiedBy ?? null,
      verifiedAt: f.verifiedAt ?? null,
    }));

  const groups: FieldGroupState[] = doc.groups.map((g) => ({
    id: g.id,
    name: g.name,
    type: g.type,
    displayOrder: g.displayOrder,
    layoutColumns: g.layoutColumns,
    isCollapsible: g.isCollapsible,
    isCollapsed: g.isCollapsedByDefault,
  }));

  // Phase 20/C3b — group raw Prisma DocumentLineItem rows by lineItemDefId and
  // map each LineItemField row into the store's LineItemFieldState shape.
  const lineItems: Record<string, import("../state/types").LineItemRow[]> = {};
  for (const raw of doc.lineItems ?? []) {
    const rows = lineItems[raw.lineItemDefId] ?? (lineItems[raw.lineItemDefId] = []);
    const fields: Record<string, import("../state/types").LineItemFieldState> = {};
    for (const f of raw.fields ?? []) {
      fields[f.columnName] = {
        id: f.id,
        columnName: f.columnName,
        value: f.keyedValue ?? f.ocrValue ?? "",
        ocrValue: f.ocrValue,
        ocrConfidence: f.ocrConfidence,
        validationStatus: (f.validationStatus as "pending" | "pass" | "fail" | "overridden") ?? "pending",
      };
    }
    rows.push({ id: raw.id, rowNumber: raw.rowNumber, fields });
  }
  // Preserve rowNumber order — Prisma returns ascending by default but be defensive.
  for (const defId of Object.keys(lineItems)) {
    lineItems[defId]!.sort((a, b) => a.rowNumber - b.rowNumber);
  }

  return {
    id: doc.id,
    // R-167 WU-7 — surface doc + batch identifier fields so the header can
    // render preferredDisplayLabel for both. Missing fields normalize to
    // null (matching the store's `string | null` type).
    displayId: doc.displayId ?? null,
    customDisplayLabel: doc.customDisplayLabel ?? null,
    batchId: doc.batchId,
    batchDisplayId: doc.batch?.displayId ?? null,
    batchCustomDisplayLabel: doc.batch?.customDisplayLabel ?? null,
    // R-90 — never let `undefined` reach the store's jobTypeId (stale /
    // 0-field stuck docs can arrive with a missing `batch`); normalize to
    // null so useCurrentJobType's falsy guard skips the fetch.
    jobTypeId: doc.batch?.jobTypeId ?? null,
    batchName: doc.batch?.name ?? "",
    filename: doc.filename,
    classification: doc.classification,
    docIndex: doc.docIndex,
    totalDocs: doc.totalDocs,
    currentStep: doc.currentStep,
    // F-046 — thread the step's re-verification policy into the store.
    stepReverifyPolicy: doc.stepReverifyPolicy ?? "standard",
    pages,
    fields,
    groups,
    lineItems,
    lineItemDefinitions: doc.lineItemDefinitions ?? [],
  };
}
